/*
  Highlights slider: scrollable pill row with prev/next arrows that move one item at a time.
  Arrows get `is-hidden` when there is nothing more to scroll in that direction.
  On desktop the row is narrowed to fit whole items only (max `data-max-visible`),
  so no pill is cut off on first view. Items stay plain links (no click interception).
*/
if (!customElements.get('highlights-slider')) {
  customElements.define(
    'highlights-slider',
    class HighlightsSlider extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-highlights-track]');
        this.prev = this.querySelector('[data-highlights-prev]');
        this.next = this.querySelector('[data-highlights-next]');
        if (!this.track || !this.prev || !this.next) return;

        this.maxVisible = parseInt(this.dataset.maxVisible, 10) || 0;
        this.desktopQuery = window.matchMedia('(min-width: 750px)');
        this.update = this.update.bind(this);
        this.onScroll = () => requestAnimationFrame(() => this.updateArrows());

        this.prev.addEventListener('click', () => this.step(-1));
        this.next.addEventListener('click', () => this.step(1));
        this.track.addEventListener('scroll', this.onScroll, {passive: true});

        // Re-measure when the container or any pill changes size (viewport, web fonts loading)
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(() => requestAnimationFrame(this.update));
          this.resizeObserver.observe(this.parentElement);
          [...this.track.children].forEach((item) => this.resizeObserver.observe(item));
        }
        window.addEventListener('resize', this.update);
        document.fonts?.ready.then(this.update);

        this.update();
      }

      disconnectedCallback() {
        this.track?.removeEventListener('scroll', this.onScroll);
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.update);
      }

      // Left edge of each item in track scroll coordinates
      itemOffsets() {
        const trackLeft = this.track.getBoundingClientRect().left - this.track.scrollLeft;
        return [...this.track.children].map((item) => item.getBoundingClientRect().left - trackLeft);
      }

      step(direction) {
        const current = this.track.scrollLeft;
        const startPadding = parseFloat(getComputedStyle(this.track).paddingLeft) || 0;
        const offsets = this.itemOffsets().map((offset) => offset - startPadding);
        let target;

        if (direction > 0) {
          target = offsets.find((offset) => offset > current + 1);
        } else {
          target = offsets.filter((offset) => offset < current - 1).pop();
        }

        if (target === undefined) target = direction > 0 ? this.track.scrollWidth : 0;

        this.track.scrollTo({left: Math.max(0, target), behavior: 'smooth'});
      }

      // Desktop: limit the row width to the first N whole items that fit (N <= maxVisible)
      fitWidth() {
        this.style.maxWidth = '';
        this.style.marginInline = '';
        if (!this.maxVisible || !this.desktopQuery.matches) return;

        const items = [...this.track.children];
        const style = getComputedStyle(this.track);
        const gap = parseFloat(style.columnGap) || 0;
        const padding = (parseFloat(style.paddingLeft) || 0) + (parseFloat(style.paddingRight) || 0);
        const available = this.track.clientWidth;
        let width = padding;
        let fitted = 0;

        for (const item of items) {
          const next = width + (fitted ? gap : 0) + item.getBoundingClientRect().width;
          if (next > available + 0.5 || fitted >= this.maxVisible) break;
          width = next;
          fitted += 1;
        }

        if (fitted === 0 || fitted === items.length) return;

        this.style.maxWidth = `${Math.ceil(width)}px`;
        this.style.marginInline = 'auto';
      }

      update() {
        this.fitWidth();
        this.updateArrows();
      }

      updateArrows() {
        const {scrollLeft, clientWidth, scrollWidth} = this.track;
        const canPrev = scrollLeft > 1;
        const canNext = scrollLeft + clientWidth < scrollWidth - 1;

        this.prev.classList.toggle('is-hidden', !canPrev);
        this.next.classList.toggle('is-hidden', !canNext);
        this.classList.toggle('is-scrollable', canPrev || canNext);
      }
    }
  );
}
